import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Autoplay from 'embla-carousel-autoplay'
import { Users } from 'lucide-react'
import { FaWhatsapp } from 'react-icons/fa'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { Separator } from '@/components/ui/separator'
import SectionHeading from '@/components/SectionHeading'
import { whatsappLink } from '@/data/hotel'
import { useGSAP } from '@/lib/gsap'
import { setupReveals } from '@/lib/reveal'
import { rooms } from '@/data/rooms'
import { cn } from '@/lib/utils'
import { getFeatureIcon } from '@/utils/roomIcons'

function RoomCarousel({ room }) {
  const [api, setApi] = useState(null)
  const [current, setCurrent] = useState(0)
  const autoplay = useRef(Autoplay({ delay: 5000, stopOnMouseEnter: true, stopOnInteraction: false }))

  useEffect(() => {
    if (!api) return
    const onSelect = () => setCurrent(api.selectedScrollSnap())
    onSelect()
    api.on('select', onSelect)
    return () => api.off('select', onSelect)
  }, [api])

  return (
    <Carousel
      setApi={setApi}
      opts={{ loop: true }}
      plugins={[autoplay.current]}
      className="h-full md:absolute md:inset-0"
    >
      <CarouselContent className="ml-0 h-full">
        {room.images.map((src, index) => (
          <CarouselItem key={src} className="pl-0">
            <img
              src={src}
              alt={`${room.title} - imagem ${index + 1}`}
              className="size-full object-cover"
            />
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious variant="secondary" className="left-3 bg-background/80 backdrop-blur" />
      <CarouselNext variant="secondary" className="right-3 bg-background/80 backdrop-blur" />
      <div className="absolute inset-x-0 bottom-4 flex justify-center gap-1.5">
        {room.images.map((src, index) => (
          <button
            key={src}
            type="button"
            onClick={() => api?.scrollTo(index)}
            aria-label={`Ver imagem ${index + 1}`}
            className={cn(
              'h-1.5 rounded-full bg-white/60 transition-all',
              current === index ? 'w-6 bg-white' : 'w-1.5'
            )}
          />
        ))}
      </div>
    </Carousel>
  )
}

const GaleriaPage = () => {
  const { hash } = useLocation()
  const root = useRef(null)
  useGSAP(() => setupReveals(root.current), { scope: root })

  // Leva até o quarto escolhido quando a página é aberta a partir da Home (/galeria#id)
  useEffect(() => {
    if (!hash) return
    // Pequeno atraso para o layout (fontes e imagens) assentar antes de rolar
    const timer = setTimeout(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 200)
    return () => clearTimeout(timer)
  }, [hash])

  return (
    <div ref={root} className="py-20 md:py-24">
      <div className="container-page flex flex-col gap-16">
        <SectionHeading
          as="h1"
          eyebrow="Acomodações"
          title="Nossos quartos"
          description="Descubra o conforto e a elegância dos nossos quartos, projetados para tornar sua estadia inesquecível."
        />

        <div className="flex flex-col gap-10">
          {rooms.map((room, index) => (
            <Card
              key={room.id}
              id={room.id}
              data-reveal
              className="scroll-mt-24 py-0 shadow-lg shadow-ink/5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div
                  data-clip
                  className={cn(
                    'relative h-72 sm:h-96 md:h-auto md:min-h-[460px]',
                    index % 2 === 1 && 'md:order-last'
                  )}
                >
                  <RoomCarousel room={room} />
                </div>

                <div className="flex flex-col gap-6 p-6 sm:p-8 lg:p-10">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">{room.tagline}</Badge>
                    {room.capacity && (
                      <Badge variant="outline">
                        <Users data-icon="inline-start" />
                        {room.capacity}
                      </Badge>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <h2 data-split className="text-3xl font-bold md:text-4xl">{room.title}</h2>
                    <p className="flex items-baseline gap-2">
                      <span className="font-heading text-2xl font-bold text-primary">
                        {room.price}
                      </span>
                      <span className="text-sm text-muted-foreground">{room.priceNote}</span>
                    </p>
                  </div>

                  <p className="leading-relaxed text-muted-foreground">{room.description}</p>

                  <Separator />

                  <div className="flex flex-col gap-4">
                    <h3 className="font-sans text-xs font-bold tracking-[0.2em] text-muted-foreground uppercase">
                      Características
                    </h3>
                    <ul className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                      {room.features.map((feature) => {
                        const Icon = getFeatureIcon(feature)
                        return (
                          <li key={feature} className="flex items-start gap-2.5 text-sm">
                            <Icon className="mt-0.5 size-4 shrink-0 text-highlight" />
                            {feature}
                          </li>
                        )
                      })}
                    </ul>
                  </div>

                  <div className="mt-auto pt-2">
                    <Button asChild size="xl">
                      <a
                        href={whatsappLink(`Olá! Tenho interesse no ${room.title}.`)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FaWhatsapp data-icon="inline-start" />
                        Reservar este quarto
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}

export default GaleriaPage
